import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';
import { AgentStatusService } from 'src/core/services/agent-status/agent-status.service';
import {
  getStatusLocalizationKey,
  isPredefinedStateName,
} from 'src/core/helpers/agent-status.helper';
import { CallFacadeService } from 'src/app/shared/agent-desktop/telephony/services/call-facade.service';
import { RingtoneService } from 'src/app/shared/agent-desktop/telephony/services/ringtone.service';
import { CallTabSyncService } from 'src/app/shared/agent-desktop/telephony/services/call-tab-sync.service';
import { RejectCallFlowService } from 'src/app/shared/agent-desktop/telephony/services/reject-call-flow.service';
import { parseSipPhoneNumber } from 'src/app/shared/agent-desktop/telephony/utils/parse-sip-phone-number';
import { CallerContactResolverService } from 'src/app/shared/agent-desktop/services/caller-contact-resolver.service';
import { IncomingCallInfo } from 'src/app/shared/agent-desktop/telephony/models/incoming-call-info.model';
import { AgentStatusDropdownComponent } from '../agent-status-dropdown/agent-status-dropdown.component';
import { IncomingCallToastComponent } from 'src/app/shared/agent-desktop/components/incoming-call-toast/incoming-call-toast.component';
import { RejectFlowOwner } from 'src/app/shared/agent-desktop/telephony/models/reject-flow-owner.type';

const AGENT_DESKTOP_PATH = '/agent-desktop';
// Must match the hardcoded `key="incoming-call"` in incoming-call-toast.component.html. This
// component provides its own isolated MessageService instance (see providers below), so reusing the
// same key does not collide with AgentDesktopComponent's separate toast instance.
const INCOMING_TOAST_KEY = 'incoming-call';
const REJECT_FLOW_OWNER: RejectFlowOwner = 'status-toolbar';

/**
 * Agent status toolbar — registered once as a LeptonX NavItem, so it is present on every
 * authenticated page. Besides the status control (headset button + status dropdown), it also owns
 * the **route-independent incoming-call alert**: it renders the shared, presentational
 * `IncomingCallToastComponent` directly and drives it from `CallFacadeService`. An agent can see and
 * answer a ringing inbound call from any page, not only while the AgentDesktop route is active.
 *
 * The incoming-call concern is deliberately thin: no CRM panel — it mirrors the alerting + answer
 * half of AgentDesktopComponent's onIncomingCall$ handling, the caller's name (resolved by the shared
 * root-provided `CallerContactResolverService`), plus the decline-reason reject flow,
 * which is shared through the root-provided `RejectCallFlowService` (no duplicated state machine).
 * Rejecting therefore **stays on the current page**: the confirm + reason dialogs open right here and
 * the call is rejected without navigating anywhere. Accepting still hands the call off to
 * AgentDesktopComponent by navigating to `/agent-desktop`;
 * CallFacadeService/CallStateStore already carry the live session across that navigation
 * (root-provided singletons), so no state is lost or duplicated. It is suppressed whenever the agent
 * is already on the AgentDesktop route so it never duplicates that page's own incoming-call
 * toast/ringtone. See diagrams/03-incoming-call-flow.md.
 *
 * The incoming-call toast is rendered OUTSIDE the availability `[style.display]` gate (see the
 * template): a display:none ancestor removes descendants (including a position:fixed p-toast) from
 * the render tree, which would silently suppress the alert.
 *
 * The reject **dialog** deliberately does NOT live here — LeptonX's `.lpx-toolbar-container`
 * (`position: absolute; z-index: 1021`) is a stacking context, so a modal rendered inside it would
 * be painted below the side menu and every body-appended overlay. It is rendered by
 * `RejectCallDialogHostComponent` from `AppComponent` instead; this component only starts the flow.
 */
@Component({
  selector: 'app-agent-status-toolbar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // NOTE: the `[style.display]` availability gate is NOT on the host — it lives on the status-button
  // wrapper in the template so the incoming-call toast can stay outside it (see class doc above).
  providers: [MessageService],
  imports: [
    CommonModule,
    LocalizationModule,
    TooltipModule,
    Popover,
    AgentStatusDropdownComponent,
    IncomingCallToastComponent,
  ],
  templateUrl: './agent-status-toolbar.component.html',
  styleUrls: ['./agent-status-toolbar.component.scss'],
})
export class AgentStatusToolbarComponent implements OnInit {
  readonly agentStatus = inject(AgentStatusService);
  private readonly localization = inject(LocalizationService);
  private readonly facade = inject(CallFacadeService);
  private readonly ringtone = inject(RingtoneService);
  private readonly callTabSync = inject(CallTabSyncService);
  private readonly rejectFlow = inject(RejectCallFlowService);
  private readonly callerContactResolver = inject(CallerContactResolverService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  @ViewChild('statusPanel') statusPanel!: Popover;

  readonly statusTooltip = computed(() => {
    const name = this.agentStatus.currentStateName();
    return isPredefinedStateName(name)
      ? this.localization.instant(getStatusLocalizationKey(name))
      : name;
  });

  // ─── Route-independent incoming-call alert ────────────────────────────────────────────────────
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  private readonly onAgentDesktopRoute = computed(() =>
    this.currentUrl().startsWith(AGENT_DESKTOP_PATH),
  );

  readonly ringtoneMuted = this.ringtone.muted;
  readonly answering = signal(false);
  readonly callerDetail = signal<string | null>(null);
  /** Full name of the registered CRM contact calling; null when unknown or the contact has no name. */
  readonly callerName = signal<string | null>(null);
  readonly queueName = signal<string | null>(null);

  private readonly isInboundRinging = this.facade.isInboundRinging;

  readonly shouldAlert = computed(() => this.isInboundRinging() && !this.onAgentDesktopRoute());

  constructor() {
    this.facade.onIncomingCall$
      .pipe(
        filter(() => !this.onAgentDesktopRoute()),
        tap(info => this.alertIncomingCall(info)),
        switchMap(info => (info.isInternal ? EMPTY : this.resolveCallerName())),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(name => {
        if (this.shouldAlert()) {
          this.callerName.set(name);
        }
      });

    effect(
      () => {
        if (!this.shouldAlert()) {
          this.clearToast();
          this.ringtone.stop();
          this.answering.set(false);
          // Stale caller data would otherwise survive into the next off-route alert and, worse,
          // scope the next reject's decline-reason list to the previous call's queue.
          this.queueName.set(null);
          this.callerDetail.set(null);
          this.callerName.set(null);
          // The call ended (caller hung up, another tab answered) or the agent moved onto the
          // AgentDesktop route: an open reject dialog belongs to a call this surface no longer
          // alerts on, so close it. Ownership is read in untracked(): RingtoneService is a root
          // singleton shared with AgentDesktopComponent, so if `owner` became a dependency here,
          // AgentDesktopComponent opening its own reject dialog would re-run this body on the
          // /agent-desktop route (where shouldAlert is permanently false) and silence its ringtone.
          untracked(() => {
            if (this.rejectFlow.isOwnedBy(REJECT_FLOW_OWNER)) {
              this.rejectFlow.reset();
            }
          });
        }
      },
      { injector: this.injector },
    );
  }

  ngOnInit(): void {
    void this.agentStatus.initSession();
  }

  async onStatusSelected(stateId: number): Promise<void> {
    await this.agentStatus.requestStatusChange(stateId);
  }

  onQueuedCleared(): void {
    this.agentStatus.setQueuedNextState(null);
  }

  onAnswer(): void {
    if (this.answering()) {
      return;
    }

    this.answering.set(true);
    this.clearToast();
    this.ringtone.stop();
    this.callTabSync.notifyAnswered();
    void this.answerAfterNavigation();
  }

  private async answerAfterNavigation(): Promise<void> {
    try {
      await this.router.navigateByUrl(AGENT_DESKTOP_PATH);
    } finally {
      await this.facade.answerCall();
    }
  }

  /**
   * Opens the decline-reason flow **in place** — no navigation. The agent stays on whatever page
   * they were on. The dialog itself is rendered by `RejectCallDialogHostComponent` at body level
   * (see that component for why it cannot live inside this toolbar); this method only starts the
   * shared flow.
   */
  onReject(): void {
    if (!this.isInboundRinging()) {
      return;
    }

    this.answering.set(false);
    this.rejectFlow.start(REJECT_FLOW_OWNER, {
      queueName: this.queueName(),
      // The backdrop covers this component's toast (it is trapped in the navbar's stacking context),
      // so the caller has to be repeated inside the dialog.
      callerLabel: this.callerName() ?? this.callerDetail(),
      // Host-owned cleanup: this component's own isolated toast instance and the ringtone.
      onRejectCommitted: () => {
        this.clearToast();
        this.ringtone.stop();
      },
    });
  }

  onMuteToggle(): void {
    if (!this.ringtoneMuted()) {
      this.ringtone.toggleMute(true);
    }
    this.clearToast();
    this.ringtone.stop();
  }

  private alertIncomingCall(info: IncomingCallInfo): void {
    this.queueName.set(info.queueName);
    this.callerDetail.set(
      parseSipPhoneNumber(info.remoteUri) || info.remoteDisplayName || info.remoteUri,
    );
    this.callerName.set(null);
    this.ringtone.resetMute();
    this.showToast();
    this.ringtone.start();
  }

  private resolveCallerName(): Observable<string | null> {
    return this.callerContactResolver.resolveCurrentCaller().pipe(
      map(contact => `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || null),
      catchError(() => of(null)),
    );
  }

  private showToast(): void {
    this.messageService.clear(INCOMING_TOAST_KEY);
    this.messageService.add({
      key: INCOMING_TOAST_KEY,
      sticky: true,
      severity: 'info',
      closable: false,
      summary: this.localization.instant('AgentDesktop::Telephony:IncomingCall'),
      detail: this.callerDetail() ?? '',
    });
  }

  private clearToast(): void {
    this.messageService.clear(INCOMING_TOAST_KEY);
  }
}
